
import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Layout } from "@/components/layout/Layout";
import { ArrowLeft, Calendar, CheckCircle2, Clock, MessageSquare, MapPin, Phone } from "lucide-react";
import { ReviewList } from "@/components/reviews/ReviewList";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { ReviewItemProps } from "@/components/reviews/ReviewItem";

// Mock data
const restaurants = [
  {
    id: 1,
    title: "베가스 한식당 - 정통 한식의 맛",
    description: "라스베가스 중심부에 위치한 정통 한식당으로, 한국의 맛을 그대로 느낄 수 있는 다양한 메뉴를 제공합니다.",
    longDescription: "라스베가스 중심부에 위치한 정통 한식당인 '베가스 한식당'은 한국의 맛을 그대로 느낄 수 있는 다양한 메뉴를 제공합니다. 20년 경력의 한식 전문 쉐프가 직접 요리하는 진짜 한국 음식을 맛보세요. 김치찌개, 된장찌개부터 갈비, 불고기까지 다양한 한식 메뉴를 제공하며, 모든 반찬은 매장에서 직접 만듭니다. 가족 모임, 단체 행사, 비즈니스 미팅 등 다양한 모임에 적합한 공간을 갖추고 있으며, 한국 전통주와 함께 더욱 풍미 있는 식사를 즐길 수 있습니다.",
    badges: ["한식", "가족 경영", "예약 가능"],
    image: "/placeholder.svg",
    price: "$$",
    hours: "매일 오전 11시 - 오후 10시",
    phone: "702-123-4567",
    parking: "무료 주차 가능",
    menus: [
      "김치찌개 - $15.99",
      "된장찌개 - $14.99",
      "불고기 - $18.99",
      "갈비찜 - $24.99",
      "비빔밥 - $16.99"
    ],
    location: "123 Vegas Blvd, Las Vegas, NV 89109",
    owner: {
      name: "김철수 대표",
      since: "2010년부터 운영",
      message: "정성을 다한 한식으로 고객님들께 한국의 맛을 전해드립니다."
    },
    reviews: [
      {
        author: "한지연",
        avatar: "",
        rating: 5,
        date: new Date("2023-10-15"),
        content: "미국에서 먹어본 한식 중 가장 맛있었어요! 특히 김치찌개가 정말 한국 맛 그대로였습니다. 친절한 서비스에도 감동받았습니다."
      },
      {
        author: "최동훈",
        avatar: "",
        rating: 4,
        date: new Date("2023-09-28"),
        content: "친구들과 방문했는데, 불고기와 갈비찜이 일품이었습니다. 양도 푸짐하고 맛도 좋았어요. 다만 주말에는 사람이 많아 예약이 필수입니다."
      },
      {
        author: "김미영",
        avatar: "",
        rating: 5,
        date: new Date("2023-08-05"),
        content: "라스베가스에서 한국 음식이 그리울 때 항상 찾는 곳입니다. 정말 한국에 있는 느낌이 들 정도로 맛있어요. 비빔밥은 꼭 드셔보세요!"
      }
    ]
  }
];

const TrainingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviews, setReviews] = useState<ReviewItemProps[]>([]);
  
  const restaurant = restaurants.find(r => r.id === Number(id));
  
  if (!restaurant) {
    return (
      <Layout>
        <div className="container py-8 max-w-screen-lg text-center">
          <h1 className="text-2xl font-bold mb-4">식당 정보를 찾을 수 없습니다</h1>
          <Button onClick={() => navigate("/business-tutoring")}>목록으로 돌아가기</Button>
        </div>
      </Layout>
    );
  }
  
  // Combine mock reviews with user-added reviews
  const allReviews = [...(restaurant.reviews || []), ...reviews];
  
  const handleReviewSubmit = (rating: number, content: string) => {
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      const newReview: ReviewItemProps = {
        author: "사용자", // In a real app, this would be the logged-in user
        avatar: "",
        rating,
        date: new Date(),
        content
      };
      
      setReviews(prev => [newReview, ...prev]);
      setIsSubmitting(false);
    }, 1000);
  };
  
  return (
    <Layout>
      <div className="container py-8 max-w-screen-lg">
        <Button 
          variant="ghost" 
          className="mb-6" 
          onClick={() => navigate("/business-tutoring")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          목록으로 돌아가기
        </Button>
        
        <div 
          className="w-full h-60 rounded-lg mb-6 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${restaurant.image})`,
            background: "linear-gradient(90deg, hsla(221, 45%, 73%, 1) 0%, hsla(220, 78%, 29%, 1) 100%)"
          }}
        >
          <div className="w-full h-full flex items-center justify-center">
            <h1 className="text-3xl font-bold text-white">{restaurant.title}</h1>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="flex flex-wrap gap-2 mb-4">
              {restaurant.badges.map((badge, index) => (
                <Badge key={index}>{badge}</Badge>
              ))}
            </div>
            
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-4">식당 소개</h2>
                <p className="text-muted-foreground whitespace-pre-line">{restaurant.longDescription}</p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-3">대표 메뉴</h3>
                <ul className="space-y-2">
                  {restaurant.menus.map((menu, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary mr-2 shrink-0 mt-0.5" />
                      <span>{menu}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-3">대표 인사말</h3>
                <div className="bg-secondary/30 rounded-lg p-4">
                  <p className="font-medium text-lg mb-2">{restaurant.owner.name}</p>
                  <div className="mb-2">
                    <p className="text-muted-foreground">{restaurant.owner.since}</p>
                  </div>
                  <p className="italic">"{restaurant.owner.message}"</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-3">위치</h3>
                <p className="text-muted-foreground">{restaurant.location}</p>
              </div>
              
              <div className="mt-8">
                <ReviewList reviews={allReviews} title="방문자 후기" />
              </div>
              
              <div className="mt-6">
                <ReviewForm onSubmit={handleReviewSubmit} isSubmitting={isSubmitting} />
              </div>
            </div>
          </div>
          
          <div>
            <Card className="sticky top-6">
              <CardContent className="pt-6">
                <div className="text-2xl font-bold mb-4">{restaurant.price}</div>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-muted-foreground mr-3" />
                    <span>{restaurant.hours}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-muted-foreground mr-3" />
                    <span>{restaurant.phone}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-muted-foreground mr-3" />
                    <span>{restaurant.parking}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <Button className="w-full">예약하기</Button>
                  <Button variant="outline" className="w-full">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    문의하기
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TrainingDetail;
